// === GALLERY SYSTEM - ADVANCED PHOTO MANAGEMENT === //

class GalleryManager {
    constructor() {
        this.albums = new Map();
        this.currentAlbum = null;
        this.swiper = null;
        this.isFullscreen = false;
        this.autoplayInterval = null;
        
        this.init();
    }
    
    init() {
        this.loadAlbumData();
        this.bindEvents();
        this.setupKeyboardNavigation();
    }
    
    loadAlbumData() {
        // Get photos based on configuration
        const getPhotosForAlbum = (albumName) => {
            const config = WEBSITE_CONFIG.photos;
            const albumPhotos = PHOTO_DATABASE[albumName] || [];
            
            // If placeholder photos are enabled, always use them (overrides everything else)
            if (config.usePlaceholderPhotos) {
                return this.generatePlaceholderPhotos(albumName);
            }
            
            // If using iCloud and we have iCloud links
            if (config.storageMethod === 'icloud' && config.icloudAlbums[albumName]) {
                return albumPhotos.map((photo, index) => ({
                    id: photo.id || index + 1,
                    src: config.icloudAlbums[albumName] + `#${index}`,
                    thumbnail: config.icloudAlbums[albumName] + `#${index}`,
                    title: photo.title || `${albumName} Photo ${index + 1}`,
                    description: photo.description || `Amazing ${albumName} moment!`,
                    date: photo.date || new Date().toISOString().split('T')[0],
                    location: photo.location || 'Unknown',
                    tags: photo.tags || [albumName],
                    likes: 0,
                    comments: []
                }));
            }
            
            // If using Google Photos
            if (config.storageMethod === 'google' && config.googlePhotos[albumName]) {
                return albumPhotos.map((photo, index) => ({
                    id: photo.id || index + 1,
                    src: config.googlePhotos[albumName] + `&index=${index}`,
                    thumbnail: config.googlePhotos[albumName] + `&index=${index}`,
                    title: photo.title || `${albumName} Photo ${index + 1}`,
                    description: photo.description || `Amazing ${albumName} moment!`,
                    date: photo.date || new Date().toISOString().split('T')[0],
                    location: photo.location || 'Unknown',
                    tags: photo.tags || [albumName],
                    likes: 0,
                    comments: []
                }));
            }
            
            // If using local storage and we have real photos, use them
            if (config.storageMethod === 'local' && albumPhotos.length > 0) {
                return albumPhotos.map(photo => ({
                    id: photo.id,
                    src: photo.src,
                    thumbnail: photo.thumbnail,
                    title: photo.title,
                    description: photo.description,
                    date: photo.date,
                    location: photo.location,
                    tags: photo.tags,
                    likes: 0,
                    comments: []
                }));
            }
            
            // Default: return empty array if no photos
            return [];
        };

        // Load album data based on configuration
        const albumData = {
            family: {
                title: '👨‍👩‍👦 Family Space Adventures',
                description: 'Fun times with my favorite people!',
                coverImage: 'assets/albums/family-cover.svg',
                photos: getPhotosForAlbum('family')
            },
            school: {
                title: '🎒 Learning Galaxy',
                description: 'Learning and playing with friends!',
                coverImage: 'assets/albums/school-cover.svg',
                photos: getPhotosForAlbum('school')
            },
            hobbies: {
                title: '🔭 My Space Hobbies',
                description: 'Things I love to do!',
                coverImage: 'assets/albums/hobbies-cover.svg',
                photos: getPhotosForAlbum('hobbies')
            },
            special: {
                title: '✨ Cosmic Celebrations',
                description: 'Birthdays, holidays & stellar achievements!',
                coverImage: 'assets/albums/special-cover.svg',
                photos: getPhotosForAlbum('special')
            }
        };
        
        // Store albums in the Map
        Object.entries(albumData).forEach(([key, data]) => {
            this.albums.set(key, data);
        });
    }
    
    generatePlaceholderPhotos(albumName) {
        // Generate placeholder photos for empty albums
        const placeholderData = {
            family: [
                { title: 'Park Day Fun', description: 'Playing at the park with Mom and Dad! 🏞️', location: 'Central Park' },
                { title: 'Family Dinner', description: 'Yummy dinner with my favorite people! 🍽️', location: 'Home' },
                { title: 'Beach Adventure', description: 'Building sandcastles at the beach! 🏖️', location: 'Sunny Beach' }
            ],
            school: [
                { title: 'First Day Magic', description: 'My first day of school was so exciting! 📚', location: 'School' },
                { title: 'Science Volcano', description: 'My volcano erupted perfectly! 🌋', location: 'Science Lab' }
            ],
            hobbies: [
                { title: 'Dancing Queen', description: 'Learning my favorite dance moves! 💃', location: 'Dance Studio' },
                { title: 'Art Creation', description: 'Painting my masterpiece! 🎨', location: 'Art Studio' }
            ],
            special: [
                { title: 'Birthday Magic', description: 'My amazing birthday party! 🎂', location: 'Home' }
            ]
        };
        
        const albumPlaceholders = placeholderData[albumName] || [];
        return albumPlaceholders.map((photo, index) => ({
            id: index + 1,
            src: `https://picsum.photos/800/600?random=${albumName}${index + 1}`,
            thumbnail: `https://picsum.photos/300/200?random=${albumName}${index + 1}`,
            title: photo.title,
            description: photo.description,
            date: new Date().toISOString().split('T')[0],
            location: photo.location,
            tags: [albumName],
            likes: Math.floor(Math.random() * 20) + 5,
            comments: ['Amazing!', 'So beautiful!']
        }));
    }
    
    bindEvents() {
        // Album card clicks
        document.querySelectorAll('.album-card').forEach(card => {
            card.addEventListener('click', (e) => {
                const albumId = card.closest('[data-album]').dataset.album;
                this.openAlbum(albumId);
            });
        });
        
        // Gallery modal events
        const modal = document.getElementById('galleryModal');
        if (modal) {
            // Close button
            const closeBtn = modal.querySelector('.gallery-close');
            if (closeBtn) {
                closeBtn.addEventListener('click', () => this.closeGallery());
            }
            
            // Outside click
            modal.addEventListener('click', (e) => {
                if (e.target === modal) {
                    this.closeGallery();
                }
            });
        }
        
        // Fullscreen toggle
        document.addEventListener('click', (e) => {
            if (e.target.classList.contains('fullscreen-btn')) {
                this.toggleFullscreen();
            }
        });
        
        // Photo actions
        document.addEventListener('click', (e) => {
            if (e.target.classList.contains('like-btn')) {
                this.toggleLike(e.target);
            }
            if (e.target.classList.contains('share-btn')) {
                this.sharePhoto(e.target);
            }
            if (e.target.classList.contains('download-btn')) {
                this.downloadPhoto(e.target);
            }
        });
    }
    
    setupKeyboardNavigation() {
        document.addEventListener('keydown', (e) => {
            if (!this.currentAlbum) return;
            
            switch(e.key) {
                case 'Escape':
                    this.closeGallery();
                    break;
                case 'ArrowLeft':
                    if (this.swiper) this.swiper.slidePrev();
                    break;
                case 'ArrowRight':
                    if (this.swiper) this.swiper.slideNext();
                    break;
                case 'f':
                case 'F':
                    this.toggleFullscreen();
                    break;
                case ' ':
                    e.preventDefault();
                    this.toggleSlideshow();
                    break;
            }
        });
    }
    
    openAlbum(albumId) {
        const album = this.albums.get(albumId);
        if (!album) {
            console.error('Album not found:', albumId);
            return;
        }
        
        this.currentAlbum = album;
        this.renderGallery(album);
        this.showModal();
    }
    
    renderGallery(album) {
        const modal = document.getElementById('galleryModal');
        if (!modal) return;
        
        // Update title
        const titleEl = modal.querySelector('#galleryTitle');
        if (titleEl) {
            titleEl.textContent = album.title;
        }
        
        // Update photo count
        const countEl = modal.querySelector('#galleryCount');
        if (countEl) {
            countEl.textContent = `1 of ${album.photos.length}`;
        }
        
        // Create gallery slides
        const wrapper = modal.querySelector('#galleryWrapper');
        if (!wrapper) return;
        
        wrapper.innerHTML = album.photos.map(photo => this.createPhotoSlide(photo)).join('');
        
        // Initialize Swiper
        this.initializeSwiper();
        
        // Add gallery controls
        this.addGalleryControls(modal);
    }
    
    createPhotoSlide(photo) {
        return `
            <div class="swiper-slide" data-photo-id="${photo.id}">
                <div class="photo-container">
                    <img src="${photo.src}" alt="${photo.title}" loading="lazy" class="gallery-photo">
                    <div class="photo-loading">
                        <div class="loading-spinner"></div>
                    </div>
                </div>
                <div class="photo-info">
                    <div class="photo-header">
                        <div class="photo-meta">
                            <h3 class="photo-title">${photo.title}</h3>
                            <div class="photo-stats">
                                <span class="photo-date">${this.formatDate(photo.date)}</span>
                                <span class="photo-location">📍 ${photo.location}</span>
                            </div>
                        </div>
                        <div class="photo-actions">
                            <button class="action-btn like-btn ${photo.likes > 10 ? 'liked' : ''}" data-photo-id="${photo.id}">
                                <i class="fas fa-heart"></i>
                                <span>${photo.likes}</span>
                            </button>
                            <button class="action-btn share-btn" data-photo-id="${photo.id}">
                                <i class="fas fa-share"></i>
                            </button>
                            <button class="action-btn download-btn" data-photo-id="${photo.id}">
                                <i class="fas fa-download"></i>
                            </button>
                            <button class="action-btn fullscreen-btn">
                                <i class="fas fa-expand"></i>
                            </button>
                        </div>
                    </div>
                    <div class="photo-description">
                        <p>${photo.description}</p>
                    </div>
                    <div class="photo-tags">
                        ${photo.tags.map(tag => `<span class="tag">#${tag}</span>`).join('')}
                    </div>
                    ${photo.comments && photo.comments.length > 0 ? `
                        <div class="photo-comments">
                            <h4>Comments:</h4>
                            ${photo.comments.map(comment => `<div class="comment">💬 ${comment}</div>`).join('')}
                        </div>
                    ` : ''}
                </div>
            </div>
        `;
    }
    
    addGalleryControls(modal) {
        // Check if controls already exist
        if (modal.querySelector('.gallery-controls')) return;
        
        const controls = document.createElement('div');
        controls.className = 'gallery-controls';
        controls.innerHTML = `
            <div class="control-group">
                <button class="control-btn slideshow-btn" title="Start Slideshow (Space)">
                    <i class="fas fa-play"></i>
                </button>
                <button class="control-btn fullscreen-btn" title="Fullscreen (F)">
                    <i class="fas fa-expand"></i>
                </button>
                <button class="control-btn info-btn active" title="Toggle Info">
                    <i class="fas fa-info-circle"></i>
                </button>
            </div>
            <div class="thumbnail-strip">
                ${this.currentAlbum.photos.map((photo, index) => `
                    <div class="thumbnail ${index === 0 ? 'active' : ''}" data-slide="${index}">
                        <img src="${photo.thumbnail}" alt="${photo.title}">
                    </div>
                `).join('')}
            </div>
        `;
        
        modal.querySelector('.gallery-modal-content').appendChild(controls);
        
        // Bind control events
        this.bindControlEvents(controls);
    }
    
    bindControlEvents(controls) {
        // Slideshow button
        const slideshowBtn = controls.querySelector('.slideshow-btn');
        slideshowBtn.addEventListener('click', () => this.toggleSlideshow());
        
        // Info toggle
        const infoBtn = controls.querySelector('.info-btn');
        infoBtn.addEventListener('click', () => this.togglePhotoInfo());
        
        // Thumbnail clicks
        controls.querySelectorAll('.thumbnail').forEach((thumb, index) => {
            thumb.addEventListener('click', () => {
                if (this.swiper) {
                    this.swiper.slideTo(index);
                }
            });
        });
    }
    
    initializeSwiper() {
        if (this.swiper) {
            this.swiper.destroy(true, true);
        }
        
        if (typeof Swiper === 'undefined') {
            console.warn('Swiper not loaded');
            return;
        }
        
        this.swiper = new Swiper('.gallery-swiper', {
            loop: this.currentAlbum.photos.length > 1,
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },
            pagination: {
                el: '.swiper-pagination',
                clickable: true,
                renderBullet: function (index, className) {
                    return `<span class="${className}">${index + 1}</span>`;
                },
            },
            keyboard: {
                enabled: true,
            },
            mousewheel: {
                forceToAxis: true,
            },
            lazy: {
                loadPrevNext: true,
            },
            zoom: {
                maxRatio: 3,
                minRatio: 1,
            },
            on: {
                slideChange: () => this.onSlideChange(),
                lazyImageLoad: (swiper, slideEl, imageEl) => {
                    const loadingEl = slideEl.querySelector('.photo-loading');
                    if (loadingEl) {
                        loadingEl.style.display = 'none';
                    }
                }
            }
        });
    }
    
    onSlideChange() {
        if (!this.swiper) return;
        
        const currentIndex = this.swiper.realIndex;
        const totalSlides = this.currentAlbum.photos.length;
        
        // Update counter
        const countEl = document.querySelector('#galleryCount');
        if (countEl) {
            countEl.textContent = `${currentIndex + 1} of ${totalSlides}`;
        }
        
        // Update active thumbnail
        document.querySelectorAll('.thumbnail').forEach((thumb, index) => {
            thumb.classList.toggle('active', index === currentIndex);
        });
        
        // Update URL hash for direct linking
        window.history.replaceState(null, null, `#photo-${this.currentAlbum.photos[currentIndex].id}`);
    }
    
    showModal() {
        const modal = document.getElementById('galleryModal');
        if (modal) {
            modal.classList.add('active');
            document.body.style.overflow = 'hidden';
            
            // Add entrance animation
            setTimeout(() => {
                modal.style.opacity = '1';
            }, 10);
        }
    }
    
    closeGallery() {
        const modal = document.getElementById('galleryModal');
        if (modal) {
            modal.classList.remove('active');
            document.body.style.overflow = 'auto';
            
            // Clean up
            if (this.swiper) {
                this.swiper.destroy(true, true);
                this.swiper = null;
            }
            
            this.currentAlbum = null;
            this.stopSlideshow();
            
            // Clear URL hash
            window.history.replaceState(null, null, window.location.pathname);
        }
    }
    
    toggleFullscreen() {
        if (!document.fullscreenElement) {
            const modal = document.getElementById('galleryModal');
            if (modal.requestFullscreen) {
                modal.requestFullscreen();
                this.isFullscreen = true;
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
                this.isFullscreen = false;
            }
        }
        
        // Update button icon
        const fullscreenBtns = document.querySelectorAll('.fullscreen-btn i');
        fullscreenBtns.forEach(icon => {
            icon.className = this.isFullscreen ? 'fas fa-compress' : 'fas fa-expand';
        });
    }
    
    toggleSlideshow() {
        if (this.autoplayInterval) {
            this.stopSlideshow();
        } else {
            this.startSlideshow();
        }
    }
    
    startSlideshow() {
        if (!this.swiper) return;
        
        this.autoplayInterval = setInterval(() => {
            this.swiper.slideNext();
        }, 3000);
        
        // Update button
        const slideshowBtn = document.querySelector('.slideshow-btn');
        if (slideshowBtn) {
            slideshowBtn.innerHTML = '<i class="fas fa-pause"></i>';
            slideshowBtn.title = 'Pause Slideshow';
        }
        
        // Show notification
        this.showToast('Slideshow started! Press Space to pause.', 'info');
    }
    
    stopSlideshow() {
        if (this.autoplayInterval) {
            clearInterval(this.autoplayInterval);
            this.autoplayInterval = null;
        }
        
        // Update button
        const slideshowBtn = document.querySelector('.slideshow-btn');
        if (slideshowBtn) {
            slideshowBtn.innerHTML = '<i class="fas fa-play"></i>';
            slideshowBtn.title = 'Start Slideshow';
        }
    }
    
    togglePhotoInfo() {
        const infoElements = document.querySelectorAll('.photo-info');
        const infoBtn = document.querySelector('.info-btn');
        
        infoElements.forEach(info => {
            info.style.display = info.style.display === 'none' ? 'block' : 'none';
        });
        
        if (infoBtn) {
            infoBtn.classList.toggle('active');
        }
    }
    
    toggleLike(button) {
        const photoId = parseInt(button.dataset.photoId);
        const isLiked = button.classList.contains('liked');
        
        // Find photo in current album
        const photo = this.currentAlbum.photos.find(p => p.id === photoId);
        if (!photo) return;
        
        // Update likes count
        photo.likes += isLiked ? -1 : 1;
        
        // Update UI
        button.classList.toggle('liked');
        button.querySelector('span').textContent = photo.likes;
        
        // Show animation
        this.createHeartAnimation(button);
        
        // Show toast
        this.showToast(isLiked ? 'Like removed!' : 'Photo liked! ❤️', 'success');
    }
    
    sharePhoto(button) {
        const photoId = parseInt(button.dataset.photoId);
        const photo = this.currentAlbum.photos.find(p => p.id === photoId);
        
        if (!photo) return;
        
        if (navigator.share) {
            navigator.share({
                title: photo.title,
                text: photo.description,
                url: window.location.href + `#photo-${photoId}`
            });
        } else {
            // Fallback - copy link to clipboard
            const url = window.location.href + `#photo-${photoId}`;
            navigator.clipboard.writeText(url).then(() => {
                this.showToast('Link copied to clipboard! 📋', 'success');
            });
        }
    }
    
    downloadPhoto(button) {
        const photoId = parseInt(button.dataset.photoId);
        const photo = this.currentAlbum.photos.find(p => p.id === photoId);
        
        if (!photo) return;
        
        // Create download link
        const link = document.createElement('a');
        link.href = photo.src;
        link.download = `${photo.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.jpg`;
        link.click();
        
        this.showToast('Photo download started! 📥', 'success');
    }
    
    createHeartAnimation(button) {
        const heart = document.createElement('div');
        heart.innerHTML = '❤️';
        heart.style.cssText = `
            position: absolute;
            pointer-events: none;
            font-size: 1.5rem;
            animation: heart-float 1s ease-out forwards;
            z-index: 1000;
        `;
        
        button.style.position = 'relative';
        button.appendChild(heart);
        
        setTimeout(() => {
            if (heart.parentNode) {
                heart.parentNode.removeChild(heart);
            }
        }, 1000);
    }
    
    showToast(message, type = 'info') {
        const toast = document.createElement('div');
        toast.className = `gallery-toast ${type}`;
        toast.textContent = message;
        toast.style.cssText = `
            position: fixed;
            top: 20px;
            left: 50%;
            transform: translateX(-50%);
            background: var(--white);
            color: var(--dark-gray);
            padding: 0.75rem 1.5rem;
            border-radius: var(--radius-lg);
            box-shadow: var(--shadow-lg);
            z-index: 10000;
            animation: toast-slide-down 0.3s ease;
            font-weight: 500;
        `;
        
        document.body.appendChild(toast);
        
        setTimeout(() => {
            toast.style.animation = 'toast-slide-up 0.3s ease forwards';
            setTimeout(() => {
                if (toast.parentNode) {
                    document.body.removeChild(toast);
                }
            }, 300);
        }, 2000);
    }
    
    formatDate(dateString) {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
        });
    }
    
    // Public API methods
    getAlbum(albumId) {
        return this.albums.get(albumId);
    }
    
    addPhoto(albumId, photoData) {
        const album = this.albums.get(albumId);
        if (album) {
            album.photos.push({
                id: Date.now(),
                ...photoData
            });
        }
    }
    
    removePhoto(albumId, photoId) {
        const album = this.albums.get(albumId);
        if (album) {
            album.photos = album.photos.filter(p => p.id !== photoId);
        }
    }
}

// Additional CSS for gallery features
const galleryStyles = `
    .gallery-controls {
        background: rgba(0, 0, 0, 0.8);
        padding: 1rem;
        border-radius: var(--radius-lg) var(--radius-lg) 0 0;
    }
    
    .control-group {
        display: flex;
        justify-content: center;
        gap: 1rem;
        margin-bottom: 1rem;
    }
    
    .control-btn {
        background: rgba(255, 255, 255, 0.2);
        border: none;
        color: white;
        padding: 0.75rem;
        border-radius: var(--radius-md);
        cursor: pointer;
        transition: all 0.3s ease;
        font-size: 1rem;
    }
    
    .control-btn:hover,
    .control-btn.active {
        background: var(--primary-pink);
        transform: scale(1.1);
    }
    
    .thumbnail-strip {
        display: flex;
        gap: 0.5rem;
        overflow-x: auto;
        padding: 0.5rem 0;
    }
    
    .thumbnail {
        flex-shrink: 0;
        width: 60px;
        height: 40px;
        border-radius: var(--radius-sm);
        overflow: hidden;
        cursor: pointer;
        border: 2px solid transparent;
        transition: all 0.3s ease;
    }
    
    .thumbnail.active {
        border-color: var(--primary-pink);
        transform: scale(1.1);
    }
    
    .thumbnail img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
    
    .photo-container {
        position: relative;
        height: 500px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #000;
    }
    
    .gallery-photo {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
    }
    
    .photo-loading {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        color: white;
    }
    
    .loading-spinner {
        width: 40px;
        height: 40px;
        border: 3px solid rgba(255, 255, 255, 0.3);
        border-top: 3px solid white;
        border-radius: 50%;
        animation: spin 1s linear infinite;
    }
    
    .photo-info {
        background: var(--white);
        padding: 1.5rem;
        max-height: 300px;
        overflow-y: auto;
    }
    
    .photo-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 1rem;
    }
    
    .photo-title {
        font-family: var(--font-heading);
        color: var(--primary-purple);
        margin: 0 0 0.5rem 0;
        font-size: 1.3rem;
    }
    
    .photo-stats {
        display: flex;
        gap: 1rem;
        font-size: 0.9rem;
        color: var(--gray);
    }
    
    .photo-actions {
        display: flex;
        gap: 0.5rem;
    }
    
    .action-btn {
        background: none;
        border: 1px solid var(--gray);
        color: var(--gray);
        padding: 0.5rem;
        border-radius: var(--radius-sm);
        cursor: pointer;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        gap: 0.25rem;
        font-size: 0.9rem;
    }
    
    .action-btn:hover {
        background: var(--primary-pink);
        color: white;
        border-color: var(--primary-pink);
    }
    
    .action-btn.liked {
        background: var(--primary-pink);
        color: white;
        border-color: var(--primary-pink);
    }
    
    .photo-description {
        margin-bottom: 1rem;
        line-height: 1.6;
    }
    
    .photo-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        margin-bottom: 1rem;
    }
    
    .tag {
        background: var(--primary-blue);
        color: white;
        padding: 0.25rem 0.5rem;
        border-radius: var(--radius-sm);
        font-size: 0.8rem;
        font-weight: 500;
    }
    
    .photo-comments h4 {
        color: var(--primary-purple);
        margin-bottom: 0.5rem;
        font-size: 1rem;
    }
    
    .comment {
        background: var(--light-gray);
        padding: 0.5rem;
        border-radius: var(--radius-sm);
        margin-bottom: 0.5rem;
        font-size: 0.9rem;
    }
    
    @keyframes heart-float {
        0% {
            opacity: 1;
            transform: translateY(0) scale(1);
        }
        100% {
            opacity: 0;
            transform: translateY(-30px) scale(1.5);
        }
    }
    
    @keyframes toast-slide-down {
        from {
            opacity: 0;
            transform: translateX(-50%) translateY(-20px);
        }
        to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
        }
    }
    
    @keyframes toast-slide-up {
        from {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
        }
        to {
            opacity: 0;
            transform: translateX(-50%) translateY(-20px);
        }
    }
    
    /* Mobile responsive */
    @media (max-width: 767px) {
        .photo-container {
            height: 300px;
        }
        
        .photo-header {
            flex-direction: column;
            gap: 1rem;
        }
        
        .photo-actions {
            justify-content: center;
        }
        
        .control-group {
            flex-wrap: wrap;
        }
        
        .thumbnail-strip {
            justify-content: center;
        }
        
        .thumbnail {
            width: 50px;
            height: 35px;
        }
    }
`;

// Add the gallery styles to the page
const galleryStyleSheet = document.createElement('style');
galleryStyleSheet.textContent = galleryStyles;
document.head.appendChild(galleryStyleSheet);

// Initialize gallery when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    window.galleryManager = new GalleryManager();
});

// Export for potential module usage
if (typeof module !== 'undefined' && module.exports) {
    module.exports = GalleryManager;
}
